import {create} from 'zustand'
import {STORAGE_KEYS} from '@/shared/config'
import * as authApi from '../api/authApi'

function isTokenValid(token: string): boolean {
  try {
    const part = token.split('.')[1]
    if (!part) return false
    const payload = JSON.parse(atob(part))
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now()
  } catch {
    return false
  }
}

function getStoredToken(): string | null {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
  if (!token) return null
  return token
}

function getStoredUserEmail(): string | null {
  return localStorage.getItem(STORAGE_KEYS.USER_EMAIL)
}

function clearAuthStorage(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.USER_EMAIL)
}

function emailFromJwt(token: string): string | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const payload = JSON.parse(atob(part))
    if (typeof payload.email === 'string') return payload.email
    return null
  } catch {
    return null
  }
}

interface AuthState {
  token: string | null
  userEmail: string | null
  isLoading: boolean
  isHydrated: boolean
  isAuthenticated: boolean
  setToken: (token: string | null) => void
  clearAuth: () => void
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => void
  hydrate: () => Promise<void>
  createSessionFromToken: (verifyToken: string) => Promise<void>
  refreshToken: () => Promise<void>
}

export const useAuthStore = create<AuthState>(set => ({
  token: null,
  userEmail: null,
  isLoading: false,
  isHydrated: false,
  isAuthenticated: false,
  setToken: token => set({token, isAuthenticated: token !== null && isTokenValid(token)}),

  clearAuth: () => {
    clearAuthStorage()
    set({token: null, userEmail: null, isAuthenticated: false})
  },

  login: async (email, password) => {
    set({isLoading: true})
    try {
      const {access_token} = await authApi.login({email, password})
      localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email)
      set({token: access_token, userEmail: email, isLoading: false, isAuthenticated: true})
    } catch (e) {
      clearAuthStorage()
      set({token: null, userEmail: null, isLoading: false, isAuthenticated: false})
      throw e
    }
  },

  register: async (email, password) => {
    set({isLoading: true})
    try {
      await authApi.register({email, password})
      localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email)
      set({userEmail: email, isLoading: false})
    } catch (e) {
      set({isLoading: false})
      throw e
    }
  },

  logout: () => {
    clearAuthStorage()
    set({token: null, userEmail: null, isAuthenticated: false})
    authApi.logout().catch(() => {})
  },

  hydrate: async () => {
    const token = getStoredToken()

    if (token && isTokenValid(token)) {
      const userEmail = getStoredUserEmail()
      set({token, userEmail, isAuthenticated: true, isHydrated: true})
      return
    }

    try {
      const {access_token} = await authApi.refreshToken()
      const email = getStoredUserEmail() || emailFromJwt(access_token)
      if (email) localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email)
      set({token: access_token, userEmail: email, isAuthenticated: true, isHydrated: true})
    } catch {
      clearAuthStorage()
      set({token: null, userEmail: null, isAuthenticated: false, isHydrated: true})
    }
  },

  createSessionFromToken: async (verifyToken: string) => {
    set({isLoading: true})
    try {
      const {access_token} = await authApi.createSession(verifyToken)
      const email = getStoredUserEmail() || emailFromJwt(access_token)
      if (email) localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email)
      set({token: access_token, userEmail: email, isLoading: false, isAuthenticated: true})
    } catch (e) {
      clearAuthStorage()
      set({token: null, userEmail: null, isLoading: false, isAuthenticated: false})
      throw e
    }
  },

  refreshToken: async () => {
    try {
      const {access_token} = await authApi.refreshToken()
      const email = getStoredUserEmail() || emailFromJwt(access_token)
      if (email) localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email)
      set({token: access_token, userEmail: email, isAuthenticated: true})
    } catch (e) {
      clearAuthStorage()
      set({token: null, userEmail: null, isAuthenticated: false})
      throw e
    }
  },
}))
